/**
 * Feature Flags Configuration
 * Controls which features are enabled in different environments
 *
 * Environment Detection:
 * - Uses REACT_APP_ENV if set explicitly
 * - Auto-detects from REACT_APP_API_URL
 * - Defaults to 'development' if neither is set
 *
 * Usage:
 *   import { isFeatureEnabled } from 'config/features'
 *
 *   if (isFeatureEnabled('PAYPAL_SUPPORT')) {
 *     // Show PayPal option
 *   }
 */

// Auto-detect environment based on API URL (no build changes needed!)
const detectEnvironment = () => {
  // If explicitly set, use that
  if (process.env.REACT_APP_ENV) {
    return process.env.REACT_APP_ENV
  }

  // Auto-detect from API URL
  const apiUrl = process.env.REACT_APP_API_URL || ""
  if(apiUrl.includes("dev-api")) return "development"
  if (apiUrl.includes("qa-api")) return "qa"
  if (apiUrl.includes("staging-api")) return "staging"
  if (apiUrl.includes("localhost") || apiUrl.includes("127.0.0.1") || apiUrl.includes("192.168") || apiUrl.includes("ngrok"))
    return "development"
  if (apiUrl.includes("api.ds.guide") || apiUrl.includes("production-api.ds.guide")) return "production" // production API

  // Default to development
  return process.env.NODE_ENV || "development"
}

const ENV = detectEnvironment()

// Feature configuration per environment
const FEATURES = {
  development: {
    EDIT_BOOKING: true,
    PAYPAL_SUPPORT: true,
    AI_DASHBOARD: true,
    NEW_RESOURCES: true,
    AI_CHATBOT_WIDGET: true,
    AI_ASSISTANT: true,
    CHECK_IN_OUT: false,
    // New embedded booking widget (#/widget/...). Off = embeds keep using #/embedded/...
    NEW_BOOKING_WIDGET: true
  },
  qa: {
    EDIT_BOOKING: true,
    PAYPAL_SUPPORT: true,
    AI_DASHBOARD: false,
    NEW_RESOURCES: true,
    AI_CHATBOT_WIDGET: false,
    AI_ASSISTANT: false,
    CHECK_IN_OUT: false,
    NEW_BOOKING_WIDGET: true
  },
  staging: {
    EDIT_BOOKING: true,
    PAYPAL_SUPPORT: true,
    AI_DASHBOARD: true,
    CUSTOM_TEMPLATE: true,
    NEW_RESOURCES: true,
    AI_CHATBOT_WIDGET: true,
    AI_ASSISTANT: true,
    CHECK_IN_OUT: false,
    NEW_BOOKING_WIDGET: false
  },
  production: {
    EDIT_BOOKING: true,
    PAYPAL_SUPPORT: true,
    AI_DASHBOARD: true,
    NEW_RESOURCES: true,
    AI_CHATBOT_WIDGET: false,
    AI_ASSISTANT: false,
    CHECK_IN_OUT: false,
    NEW_BOOKING_WIDGET: false
  }
}

/**
 * Check if a specific feature is enabled in the current environment
 * @param {string} featureName - The feature key to check
 * @returns {boolean} - Whether the feature is enabled
 */
export const isFeatureEnabled = (featureName) => {
  const envFeatures = FEATURES[ENV] || FEATURES.development
  return envFeatures[featureName] || false
}

/**
 * Get all feature flags for the current environment
 * @returns {Object} - All feature flags for current environment
 */
export const getFeatureConfig = () => {
  return FEATURES[ENV] || FEATURES.development
}

/**
 * Get the current environment name
 * @returns {string} - Current environment name
 */
export const getCurrentEnvironment = () => {
  return ENV
}

// Log current environment in development/qa for debugging
if (ENV === "development" || ENV === "qa") {
  console.log("[Feature Flags] Environment:", ENV)
  console.log("[Feature Flags] Config:", FEATURES[ENV])
}
